import type { AppConfig } from '@/config/env';

/**
 * Pixel event mapping, ported from WebPaywalls (`constants/analytics.ts`).
 *
 * The shared providers have two paths: meta events (`Install`, `First Action`) go through their
 * built-in `eventsMapper`, everything else only reaches the pixel when `eventNameMapper` has an
 * entry for the event name. WebQuiz only ever used the meta path, so the funnel — which also owns
 * the paywall — sent nothing but Install to the ad platforms.
 *
 * Keys are the funnel's own event names, prefix included.
 */
export const PAYWALL_VIEW_EVENT = '[Web View] Paywall';
export const FINISH_PURCHASE_EVENT = '[Web Action] Finish purchase';

export const tiktokEventNames: Record<string, string> = {
  [PAYWALL_VIEW_EVENT]: 'InitiateCheckout',
  [FINISH_PURCHASE_EVENT]: 'CompletePayment',
};

export const snapchatEventNames: Record<string, string> = {
  [PAYWALL_VIEW_EVENT]: 'ADD_CART',
  [FINISH_PURCHASE_EVENT]: 'PURCHASE',
};

export const googleEventNames: Record<string, string> = {
  [PAYWALL_VIEW_EVENT]: 'generate_lead',
  [FINISH_PURCHASE_EVENT]: 'purchase',
};

export const pinterestEventNames: Record<string, string> = {
  [PAYWALL_VIEW_EVENT]: 'lead',
  [FINISH_PURCHASE_EVENT]: 'checkout',
};

export const FINISH_UPSELL_PURCHASE_EVENT = '[Web Action] Finish upsell purchase';
export const USER_REGISTERED_EVENT = '[Web Action] User registered';
export const DOWNLOAD_VIEW_EVENT = '[Web View] Download';

/**
 * Jitsu names, ported from WebPaywalls' `telemetryEventsMapper`.
 *
 * Unmapped events still reach Jitsu under their raw names — this mapper renames the ones the
 * downstream reports are built on, and marks which of them carry revenue.
 */
export const telemetryEventNames: Record<string, string> = {
  [PAYWALL_VIEW_EVENT]: 'Lead',
  [FINISH_PURCHASE_EVENT]: 'Purchase',
  [FINISH_UPSELL_PURCHASE_EVENT]: 'Upsell Purchase',
  [USER_REGISTERED_EVENT]: 'Registered',
  [DOWNLOAD_VIEW_EVENT]: 'Download',
};

export const telemetryRevenueEvents = [FINISH_PURCHASE_EVENT, FINISH_UPSELL_PURCHASE_EVENT];

/**
 * Jitsu revenue: WebPaywalls charges `trialPrice || Price` converted to USD — the gross amount,
 * without the processor fee that the ad pixels subtract.
 */
export const createRevenueResolver =
  (exchangeRate: ReturnType<typeof createExchangeRate>) =>
  async (payload: PurchasePayload): Promise<number | undefined> => {
    const properties = payload.properties ?? {};
    const price = Number(properties.trialPrice || properties.Price || 0);
    if (!price) return 0;
    const currency = String(properties.currency || 'usd');
    return (await exchangeRate(currency, price)) ?? price;
  };

/**
 * Processor fees, as in WebPaywalls' PAYMENT_COMMISSION_MAPPER — the pixels report net revenue.
 * Keys are the funnel's own system/method literals; an unlisted pair costs nothing.
 */
const COMMISSION: Record<string, Record<string, (price: number) => number>> = {
  stripe: {
    card: (price) => (price / 100) * 2.9 + 0.3,
    cashapp: (price) => (price / 100) * 2.9 + 0.3,
    apple_pay: (price) => (price / 100) * 2.9 + 0.3,
    google_pay: (price) => (price / 100) * 2.9 + 0.3,
  },
  authorize: {
    card: (price) => price / 100 + 0.2,
  },
  paypal: {
    paypal: (price) => (price / 100) * 2.9 + 0.3,
  },
  braintree: {
    card: (price) => (price / 100) * 4 + (price / 100) * 0.06 + 0.06,
    paypal: (price) => (price / 100) * 2.78 + 0.29,
  },
};

export interface PurchasePayload {
  name: string;
  properties?: Record<string, unknown>;
}

type ExchangeRate = (currency: string, amount: number) => Promise<number | undefined>;

/** `GET /exchange-rate/convert` — same endpoint WebPaywalls converts purchase values with. */
export const createExchangeRate = (
  config: Pick<AppConfig, 'analyticsApiUrl' | 'apiUrl'>,
  logger: { logInfo: (data: unknown, message?: string) => void },
): ExchangeRate => {
  const base = (config.analyticsApiUrl || config.apiUrl || '').replace(/\/$/, '');
  const cache = new Map<string, Promise<number | undefined>>();
  return (currency, amount) => {
    if (!base || !amount) return Promise.resolve(amount || 0);
    const key = `${currency}:${amount}`;
    const cached = cache.get(key);
    if (cached) return cached;
    const pending = fetch(`${base}/exchange-rate/convert?from=${encodeURIComponent(currency)}&amount=${amount}`)
      .then((response) => (response.ok ? (response.json() as Promise<{ value?: number }>) : undefined))
      .then((data) => data?.value)
      .catch((error: unknown) => {
        logger.logInfo({ error: String(error) }, 'exchangeRateFailed');
        return undefined;
      });
    cache.set(key, pending);
    return pending;
  };
};

/**
 * Purchase value for Google/TikTok/Pinterest/Snapchat: `Price` converted to USD, minus the
 * processor fee. Kept identical to WebPaywalls' `resolvePriceValue` so the numbers do not drift
 * between the two apps. A failed conversion reports the raw price rather than nothing.
 */
export const createPurchaseValueResolver =
  (exchangeRate: ExchangeRate) =>
  async (payload: PurchasePayload): Promise<number | undefined> => {
    const properties = payload.properties ?? {};
    const price = Number(properties.Price || 0);
    if (!price) return 0;
    const currency = String(properties.currency || 'usd');
    const priceUSD = (await exchangeRate(currency, price)) ?? price;
    const commission = COMMISSION[String(properties.PaymentSystem)]?.[String(properties.provider)]?.(priceUSD) || 0;
    return Number((priceUSD - commission).toFixed(2));
  };

export const purchaseConfig = (exchangeRate: ExchangeRate) => ({
  events: [FINISH_PURCHASE_EVENT],
  resolveValue: createPurchaseValueResolver(exchangeRate),
});
